import Button from "@/components/common/button/Button";
import Datalist, { DatalistPropsType } from "@/components/common/datalist/Datalist";
import Translate from "@/components/common/Translate";
import ImageFile from "@/components/common/views/ImageFile";
import ModalPortal from "@/components/modal/ModalPortal";
import { api, baseUrl, graph } from "@/config/axios-context";
import { BookingAttachmentFilterInput, BookingAttachmentSortInput, BookingAttachmentsQuery, BookingAttachmentsQueryVariables } from "@/types/graphql";
import { Roles } from "@/utilities/app-utilities";
import { appendFilters } from "@/utilities/datalist/datalist-filter-utilities";
import useAppContext from "@/utilities/hooks/useAppContext";
import { gql } from "@apollo/client";
import { faDownload, faEye, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const query = gql`
query bookingAttachments($take: Int, $skip: Int, $filter: BookingAttachmentFilterInput, $order: [BookingAttachmentSortInput!]){
    bookingAttachments {
        items(take: $take, skip: $skip, where: $filter, order: $order) {
            id
            name,
            contentType,
            bookingId
        }
        count(where: $filter)
    }
}`;

type DataItem = NonNullable<BookingAttachmentsQuery["bookingAttachments"]>["items"][number];

const MODAL_DELETE_NAME = "booking-attachment-delete-confirmation";

type UploadState = {
    fileName: string;
    fileSize: number;
    progress: number;
};

const BookingAttachmentsPage = () => {
    const params = useParams<{ id: string }>();
    const context = useAppContext();
    const [uploadState, setUploadState] = useState<UploadState | null>(null);
    const [deletedItemId, setDeletedItemId] = useState<string | number | null>(null);
    const datalistRef = useRef<Datalist<DataItem, BookingAttachmentFilterInput, BookingAttachmentSortInput>>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const t = context.t;
    const isUploading = uploadState !== null;

    useEffect(() => {
        if (deletedItemId) {
            context.modal.open(MODAL_DELETE_NAME);
        } else {
            context.modal.close(MODAL_DELETE_NAME);
        }
    }, [deletedItemId]);

    const datalistConfig: DatalistPropsType<DataItem, BookingAttachmentFilterInput, BookingAttachmentSortInput> = {
        dataSource: {
            async request(requestParams) {
                let variables: BookingAttachmentsQueryVariables = {
                    skip: requestParams.skip,
                    take: requestParams.take,
                    order: requestParams.sort,
                    filter: requestParams.filters,
                };

                if (requestParams.searchText) {
                    const searchFilter: BookingAttachmentFilterInput = {
                        or: [
                            { name: { contains: requestParams.searchText } },
                            { contentType: { contains: requestParams.searchText } },
                        ]
                    };

                    variables.filter = appendFilters(variables.filter, searchFilter);
                }

                variables.filter = appendFilters(variables.filter, {
                    bookingId: { eq: parseInt(params.id!) }
                });

                const response = await graph<BookingAttachmentsQuery, BookingAttachmentsQueryVariables>({
                    query,
                    variables,
                });

                return {
                    items: response.data?.bookingAttachments?.items || [],
                    count: response.data?.bookingAttachments?.count || 0,
                }
            },
        },
        itemId(i) {
            return i.id;
        },
        name: "Booking_Attachments",
        table: {
            columns: [
                {
                    name: "id",
                    hide: true
                },
                {
                    name: 'icon',
                    title: '',
                    resolve(item) {
                        return <ImageFile
                            src={"/api/file/get/" + item.name}
                            alt={item.name}
                            contentType={item.contentType}
                            className="w-10 h-10 object-cover"
                        />;
                    }
                },
                {
                    name: "name",
                    className: "text-left max-w-[250px] overflow-hidden text-ellipsis",
                    card: {
                        group: 'name'
                    }
                },
                {
                    name: "contentType",
                    card: {
                        group: 'contentType'
                    }
                }
            ],
            render: {
                actions: {
                    // async delete(item, e) {
                    //     setDeletedItemId(item.id);
                    // },
                    mutationPermissionRole: Roles.ACCOMMODATION_BOOKINGS_WRITE,
                    buttons: [
                        {
                            icon: faEye,
                            name: "view",
                            title: context.t('common.view'),
                            onClick: handelAttachmentView
                        },
                        {
                            icon: faDownload,
                            name: "Download",
                            title: context.t('common.download'),
                            onClick: handleDownload
                        },
                        {
                            icon: faTrash,
                            name: "Delete",
                            title: context.t('common.delete'),
                            theme: "link danger",
                            onClick(item) {
                                setDeletedItemId(item.id);
                            }
                        }
                    ]
                },
                card(item) {
                    return (
                        <div className="border border-color rounded p-4 mb-4">
                            <div className="grid grid-cols-[auto_1fr] gap-2 p-4">
                                <div>
                                    <ImageFile
                                        src={"/api/file/get/" + item.name}
                                        alt={item.name}
                                        contentType={item.contentType}
                                        className="w-20 h-20 text-5xl object-cover"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div className="w-full font-bold whitespace-nowrap overflow-hidden text-ellipsis">{item.name}</div>
                                    <div className="text-sm text-gray-500">{item.contentType}</div>
                                </div>
                            </div>
                            <div className="w-full">
                                <div className="grid grid-cols-3 gap-4">
                                    <button onClick={() => handelAttachmentView(item)} className="bg-primary rounded text-white px-4 py-2">
                                        <FontAwesomeIcon icon={faEye} className="mr-2" />
                                    </button>
                                    <button onClick={() => handleDownload(item)} className="bg-primary rounded text-white px-4 py-2">
                                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                    </button>
                                    {context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE) && (
                                        <button onClick={() => setDeletedItemId(item.id)} className="bg-danger rounded text-white px-4 py-2">
                                            <FontAwesomeIcon icon={faTrash} className="mx-2" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                },
            }
        },
        header: {
            rightButtons: [
                {
                    icon: faUpload,
                    onClick() {
                        if (!isUploading) {
                            fileInputRef.current?.click();
                        }
                    },
                    name: "Upload",
                    title: "Upload"
                }
            ]
        }
    }

    function handelAttachmentView(attachment: DataItem) {
        window.open(baseUrl + `/api/file/get/${attachment.name}`, "_blank");
    }

    function handleDownload(attachment: DataItem) {
        window.open(baseUrl + `/api/file/download/${attachment.name}`, "_blank");
    }

    async function handelDelete() {
        try {
            const request = await api.delete(`/bookingAttachments/delete/${deletedItemId}`);
            context.notify('success', "File deleted successfully");
            datalistRef.current?.reload();
            setDeletedItemId(null);
        } catch (error) {
            console.error("Error deleting file:", error);
            context.notify('error', "An error occurred while deleting the file");
        }
    }

    async function handleUpload(file: File) {
        setUploadState({
            fileName: file.name,
            fileSize: file.size,
            progress: 0,
        });

        try {
            const response = await api.postFile(
                `bookingAttachments/upload/${params.id}`,
                file,
                {
                    onUploadProgress(event) {
                        if (!event.total) return;

                        const progress = Math.min(100, Math.round((event.loaded * 100) / event.total));
                        setUploadState(current => current ? { ...current, progress } : current);
                    },
                }
            );

            if (!response.success) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            setUploadState(current => current ? { ...current, progress: 100 } : current);
            context.notify('success', "File uploaded successfully");
            datalistRef.current?.reload();
        } catch (error) {
            console.error("Error uploading file:", error);
            context.notify('error', "An error occurred while uploading the file");
        } finally {
            setUploadState(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    }

    function formatFileSize(bytes: number) {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    return (
        <div>
            {uploadState && (
                <div
                    className="mb-4 rounded border border-color bg-white dark:bg-gray-800 p-4 shadow-sm"
                    role="status"
                    aria-live="polite"
                >
                    <div className="mb-2 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <div className="font-bold">Uploading file</div>
                            <div className="truncate text-sm text-gray-500">
                                {uploadState.fileName} ({formatFileSize(uploadState.fileSize)})
                            </div>
                        </div>
                        <div className="shrink-0 font-bold text-primary">
                            {uploadState.progress}%
                        </div>
                    </div>
                    <div
                        className="h-2 w-full overflow-hidden rounded bg-gray-200"
                        role="progressbar"
                        aria-label={`Uploading ${uploadState.fileName}`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={uploadState.progress}
                    >
                        <div
                            className="h-full bg-primary transition-all duration-200"
                            style={{ width: `${uploadState.progress}%` }}
                        />
                    </div>
                </div>
            )}
            <Datalist ref={datalistRef} {...datalistConfig} />
            <div className="absolute hidden">
                <input
                    ref={fileInputRef}
                    type="file"
                    disabled={isUploading}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            void handleUpload(file);
                        }
                    }}
                />
            </div>
            <ModalPortal name={MODAL_DELETE_NAME} title={context.t('common.confirmation')}>
                <div className="py-5">
                    <div className="w-full text-center p-10">
                        <p>{t('confirmations.deleteItremConfirmation')}</p>
                    </div>
                    <div className="grid grid-cols-2 w-max mx-auto gap-5">
                        <Button type="button" theme="primary" onClick={handelDelete} title="yes">
                            <Translate i18nKey="common.yes" />
                        </Button>
                        <Button type="button" onClick={() => context.modal.close(MODAL_DELETE_NAME)} title="no">
                            <Translate i18nKey="common.no" />
                        </Button>
                    </div>
                </div>
            </ModalPortal>
        </div>
    );
}

export default BookingAttachmentsPage;
